import { formatTransactionMoney } from '../../utils/formatters/money';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, CheckCircle2, Clock3 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const EXPERIENCE_CONTEXT = Object.freeze({
  ONBOARDING: 'onboarding',
  NEW_BANK_CONNECTION: 'new_bank_connection',
  NET_WORTH_CONNECTION: 'net_worth_connection',
  NET_WORTH_IMPORT: 'net_worth_import',
  NET_WORTH_ACCOUNT_CONNECTION: 'net_worth_account_connection',
  NET_WORTH_ACCOUNT_IMPORT: 'net_worth_account_import',
  NET_WORTH: 'net_worth',
});

const NET_WORTH_CONTEXTS = new Set([
  EXPERIENCE_CONTEXT.NET_WORTH_CONNECTION,
  EXPERIENCE_CONTEXT.NET_WORTH_IMPORT,
  EXPERIENCE_CONTEXT.NET_WORTH_ACCOUNT_CONNECTION,
  EXPERIENCE_CONTEXT.NET_WORTH_ACCOUNT_IMPORT,
  EXPERIENCE_CONTEXT.NET_WORTH,
]);

const getBannerTitleCopy = ({
  experienceContext,
  importedTransactionCount = null,
  success = false,
  activeInstitutionName = null,
}) => {
  const importedCount = Number(importedTransactionCount);
  const hasImportedCount = importedTransactionCount !== null
    && importedTransactionCount !== undefined
    && Number.isFinite(importedCount);
  const isPostOnboardingImport = experienceContext === EXPERIENCE_CONTEXT.NEW_BANK_CONNECTION
    || NET_WORTH_CONTEXTS.has(experienceContext);

  if (success) {
    if (
      isPostOnboardingImport
      && hasImportedCount
      && importedCount === 0
    ) {
      return 'Bank connected. No recent transactions were available to import.';
    }

    return isPostOnboardingImport
      ? 'New bank ready'
      : 'Your dashboard is ready';
  }

  if (isPostOnboardingImport) {
    void activeInstitutionName;
    return 'Importing Transactions';
  }

  return experienceContext === EXPERIENCE_CONTEXT.NEW_BANK_CONNECTION
    ? 'Importing Transactions'
    : 'Preparing your dashboard';
};
const CONNECTING_ACCOUNTS_COPY = 'Connecting accounts';
const RECENT_IMPORT_COPY = 'Importing recent history';
const OLDER_MONTHS_COPY = 'Importing older months';
const REFINING_CATEGORIES_COPY = 'Refining categories';
const TICKER_INTERVAL_MS = 2600;
const ACTIVE_TICKER_IDLE_RESET_MS = 5200;
const LIVE_TICKER_FRESH_MS = 15000;

const buildEventKey = (event) => `${event?.transactionId || 'unknown'}:${event?.completedAt || 'pending'}`;
const getCompletedAtMs = (event) => {
  const completedAt = new Date(event?.completedAt || 0).getTime();
  return Number.isFinite(completedAt) ? completedAt : 0;
};
const isFreshTickerEvent = (event) => (
  getCompletedAtMs(event) > 0
  && (Date.now() - getCompletedAtMs(event)) <= LIVE_TICKER_FRESH_MS
);

const sortTickerEvents = (events = []) => (
  [...events].sort((left, right) => {
    const leftTime = getCompletedAtMs(left);
    const rightTime = getCompletedAtMs(right);
    return leftTime - rightTime;
  })
);

const formatTickerAmount = (value) => value == null || value === '' || !Number.isFinite(Number(value))
  ? null : formatTransactionMoney(value, { currency: 'CAD', locale: undefined });

const resolveTickerCategoryParts = (event) => {
  if (event?.categoryName) {
    return {
      categoryName: event.categoryName,
    };
  }

  const parts = String(event?.categoryLabel || 'Categorized')
    .split(' > ')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return {
      categoryName: parts[parts.length - 1],
    };
  }

  return {
    categoryName: parts[0] || 'Categorized',
  };
};

const truncateTickerSegment = (value, maxLength) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};

const RecentCategorizationTicker = ({ events = [], isActive = false }) => {
  const { preference } = useTheme();
  const isLight = preference === 'light';
  const tickerEvents = useMemo(
    () => sortTickerEvents(Array.isArray(events) ? events.filter(Boolean) : []),
    [events]
  );
  const seenKeysRef = useRef(new Set());
  const [activeEvent, setActiveEvent] = useState(null);
  const [pendingQueue, setPendingQueue] = useState([]);

  useEffect(() => {
    if (tickerEvents.length === 0) {
      return;
    }

    const unseenEvents = [];
    tickerEvents.forEach((event) => {
      const eventKey = buildEventKey(event);
      if (seenKeysRef.current.has(eventKey)) return;
      seenKeysRef.current.add(eventKey);
      unseenEvents.push(event);
    });

    if (!activeEvent) {
      const [firstUnseen, ...remainingUnseen] = unseenEvents;
      if (firstUnseen) {
        setActiveEvent(firstUnseen);
        if (remainingUnseen.length > 0) {
          setPendingQueue((currentQueue) => [...currentQueue, ...remainingUnseen]);
        }
        return;
      }

      if (isActive) {
        return;
      }

      const latestEvent = tickerEvents[tickerEvents.length - 1];
      setActiveEvent(isFreshTickerEvent(latestEvent) ? latestEvent : null);
      return;
    }

    if (unseenEvents.length > 0) {
      setPendingQueue((currentQueue) => [...currentQueue, ...unseenEvents]);
    }
  }, [activeEvent, isActive, tickerEvents]);

  useEffect(() => {
    if (pendingQueue.length === 0) return undefined;
    const timeout = window.setTimeout(() => {
      setPendingQueue((currentQueue) => {
        if (currentQueue.length === 0) return currentQueue;
        const [nextEvent, ...remainingEvents] = currentQueue;
        setActiveEvent(nextEvent);
        return remainingEvents;
      });
    }, TICKER_INTERVAL_MS);

    return () => window.clearTimeout(timeout);
  }, [pendingQueue]);

  useEffect(() => {
    if (!activeEvent || pendingQueue.length > 0) return undefined;

    if (isActive) {
      const timeout = window.setTimeout(() => {
        setActiveEvent((currentEvent) => {
          if (!currentEvent) return null;
          return buildEventKey(currentEvent) === buildEventKey(activeEvent) ? null : currentEvent;
        });
      }, ACTIVE_TICKER_IDLE_RESET_MS);

      return () => window.clearTimeout(timeout);
    }

    const completedAtMs = getCompletedAtMs(activeEvent);
    if (!completedAtMs) {
      setActiveEvent(null);
      return undefined;
    }

    const timeRemaining = LIVE_TICKER_FRESH_MS - (Date.now() - completedAtMs);
    if (timeRemaining <= 0) {
      setActiveEvent(null);
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setActiveEvent((currentEvent) => {
        if (!currentEvent) return null;
        return buildEventKey(currentEvent) === buildEventKey(activeEvent) ? null : currentEvent;
      });
    }, timeRemaining);

    return () => window.clearTimeout(timeout);
  }, [activeEvent, isActive, pendingQueue.length]);

  const tickerTextClassName = isLight
    ? 'truncate text-[12px] font-medium text-slate-700'
    : 'truncate text-[12px] font-medium text-slate-200/92';

  if (!activeEvent) {
    return (
      <div data-testid="historical-import-live-ticker" aria-label="Recent categorization ticker" className="min-w-0">
        <div className={tickerTextClassName}>Scanning imported transactions…</div>
      </div>
    );
  }

  const merchant = activeEvent?.merchantName || 'Transaction';
  const amountLabel = formatTickerAmount(activeEvent?.amount) || null;
  const { categoryName } = resolveTickerCategoryParts(activeEvent);
  const tickerSummary = [
    truncateTickerSegment(merchant, 20),
    amountLabel,
    truncateTickerSegment(categoryName, 18),
  ].filter(Boolean).join(' • ');

  return (
    <div data-testid="historical-import-live-ticker" aria-label="Recent categorization ticker" className="min-w-0">
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={buildEventKey(activeEvent)}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          className="min-w-0"
        >
          <div className={tickerTextClassName}>{tickerSummary}</div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const HistoricalImportShellBanner = ({
  phase = null,
  recentEvents = [],
  experienceContext = EXPERIENCE_CONTEXT.ONBOARDING,
  importedTransactionCount = null,
  activeInstitutionName = null,
  queuedImportCount = 0,
  aiCategorizationEnabled = false,
}) => {
  const { preference } = useTheme();
  const isLight = preference === 'light';
  if (!phase) return null;

  const isSuccess = phase === 'success';
  const isRecentImportStep = phase === 'recent_import';
  const isHistoricalImportStep = phase === 'historical_import';
  const isAiReviewStep = phase === 'ai_review';
  const title = getBannerTitleCopy({
    experienceContext,
    importedTransactionCount,
    success: isSuccess,
    activeInstitutionName,
  });
  const queuedCount = Math.max(0, Number(queuedImportCount) || 0);
  const queueLabel = NET_WORTH_CONTEXTS.has(experienceContext) && queuedCount > 0
    ? `${queuedCount} more queued`
    : null;
  const iconClassName = isSuccess
    ? (isLight ? 'text-emerald-700' : 'text-emerald-200')
    : (isLight ? 'text-slate-700' : 'text-white');
  const bannerClassName = isLight
    ? 'w-full max-w-[590px] rounded-[30px] border border-slate-900/[0.08] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(244,247,251,0.98))] px-8 py-7 text-slate-900 shadow-[0_24px_56px_rgba(15,23,42,0.18)] backdrop-blur-xl'
    : 'w-full max-w-[590px] rounded-[30px] border border-white/[0.08] bg-[linear-gradient(180deg,rgba(18,25,42,0.98),rgba(15,21,36,0.98))] px-8 py-7 text-white shadow-[0_24px_56px_rgba(2,6,23,0.52)] backdrop-blur-xl';
  const titleTextClassName = isLight
    ? 'text-[19px] font-semibold leading-[1.1] tracking-[-0.02em] text-slate-900'
    : 'text-[19px] font-semibold leading-[1.1] tracking-[-0.02em] text-white';
  const rowTextClassName = isLight
    ? 'text-[14px] font-medium leading-[1.4] text-slate-900'
    : 'text-[14px] font-medium leading-[1.4] text-white/92';
  const mutedRowTextClassName = isLight
    ? 'text-[14px] font-medium leading-[1.4] text-slate-400'
    : 'text-[14px] font-medium leading-[1.4] text-slate-500';

  const StepStatusIcon = ({ status = 'pending' }) => {
    if (status === 'complete') {
      return (
        <span className={`flex h-4 w-4 items-center justify-center rounded-full ${
          isLight ? 'bg-emerald-500/12 text-emerald-700' : 'bg-emerald-400/12 text-emerald-300'
        }`}>
          <Check className="h-3 w-3" />
        </span>
      );
    }

    if (status === 'active') {
      return (
        <span className="inline-flex h-4 w-5 items-center justify-center gap-0.5" aria-hidden="true">
          {[0, 1, 2].map((dot) => (
            <motion.span
              key={`import-step-loader-dot-${dot}`}
              className={`h-1.5 w-1.5 rounded-full ${isLight ? 'bg-indigo-600' : 'bg-indigo-200'}`}
              animate={{ opacity: [0.35, 1, 0.35], y: [0, -2, 0] }}
              transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: dot * 0.12 }}
            />
          ))}
        </span>
      );
    }

    return (
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${isLight ? 'bg-slate-300' : 'bg-slate-600'}`} aria-hidden="true" />
    );
  };

  const renderStepRow = ({ label, status, children = null }) => (
    <div className="min-w-0">
      <div className="flex min-w-0 items-start gap-3.5">
        <div className="mt-[2px] shrink-0">
          <StepStatusIcon status={status} />
        </div>
        <div className={`min-w-0 flex-1 ${status === 'pending' ? mutedRowTextClassName : rowTextClassName}`}>{label}</div>
      </div>
      {children ? <div className="ml-[1.9rem] mt-2.5 min-w-0 pb-1">{children}</div> : null}
    </div>
  );

  return (
    <AnimatePresence initial={false} mode="wait">
      <motion.div
        key={phase}
        data-testid="historical-import-shell-banner"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className={bannerClassName}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-3.5">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center ${iconClassName}`}>
              {isSuccess ? (
                <CheckCircle2 className="h-8 w-8" />
              ) : (
                <Clock3 className="h-8 w-8" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className={`min-w-0 truncate ${titleTextClassName}`}>{title}</div>
              {activeInstitutionName && !isSuccess ? (
                <div className={`mt-1 truncate text-[13px] font-medium ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                  {activeInstitutionName}
                </div>
              ) : null}
            </div>
            {queueLabel ? (
              <span
                data-testid="historical-import-queue-chip"
                className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                  isLight ? 'border-indigo-500/20 bg-indigo-500/10 text-indigo-700' : 'border-indigo-300/20 bg-indigo-400/10 text-indigo-100'
                }`}
              >
                {queueLabel}
              </span>
            ) : null}
          </div>
          <div className="mt-5 flex min-w-0 flex-col gap-[18px]">
            {renderStepRow({
              label: CONNECTING_ACCOUNTS_COPY,
              status: 'complete',
            })}

            {renderStepRow({
              label: RECENT_IMPORT_COPY,
              status: isRecentImportStep ? 'active' : 'complete',
            })}

            {renderStepRow({
              label: OLDER_MONTHS_COPY,
              status: isRecentImportStep
                ? 'pending'
                : isHistoricalImportStep
                  ? 'active'
                  : 'complete',
            })}

            {aiCategorizationEnabled ? renderStepRow({
              label: REFINING_CATEGORIES_COPY,
              status: isAiReviewStep ? 'active' : isSuccess ? 'complete' : 'pending',
              children: (
                isAiReviewStep ? (
                  <RecentCategorizationTicker
                    events={recentEvents}
                    isActive
                  />
                ) : null
              ),
            }) : null}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default HistoricalImportShellBanner;
